import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api, { API } from "@/lib/api";
import PublicHeader from "@/components/PublicHeader";
import PublicFooter from "@/components/PublicFooter";
import { FileDown, MessageCircle, ChevronLeft, ChevronRight, Calendar as CalendarIcon, Newspaper } from "lucide-react";
import { toast } from "sonner";

const DAY_HI = ["र", "सो", "मं", "बु", "गु", "शु", "श"];
const MONTH_HI = ["जनवरी","फ़रवरी","मार्च","अप्रैल","मई","जून","जुलाई","अगस्त","सितम्बर","अक्टूबर","नवम्बर","दिसम्बर"];

function ymd(d) {
  const yr = d.getFullYear(), mo = String(d.getMonth() + 1).padStart(2, "0"), da = String(d.getDate()).padStart(2, "0");
  return `${yr}-${mo}-${da}`;
}

export default function EPaperArchivePage() {
  const [available, setAvailable] = useState({});
  const [cursor, setCursor] = useState(() => { const d = new Date(); d.setDate(1); return d; });
  const [selected, setSelected] = useState(ymd(new Date()));

  useEffect(() => {
    api.get("/epaper/available-dates?days=90").then((r) => {
      const m = {}; r.data.forEach((row) => { m[row.date] = row.count; });
      setAvailable(m);
    }).catch(() => {});
  }, []);

  const days = useMemo(() => {
    const first = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
    const startDow = first.getDay();
    const lastDate = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0).getDate();
    const cells = [];
    for (let i = 0; i < startDow; i++) cells.push(null);
    for (let d = 1; d <= lastDate; d++) cells.push(new Date(cursor.getFullYear(), cursor.getMonth(), d));
    while (cells.length % 7 !== 0) cells.push(null);
    return cells;
  }, [cursor]);

  const todayYmd = ymd(new Date());
  const isToday = selected === todayYmd;
  // For today, always use the no-date endpoint so it falls back to the latest 15
  // stories when nothing has been published yet today. Past dates use strict filter.
  const pdfUrl = isToday ? `${API}/epaper/today` : `${API}/epaper/today?date=${selected}`;
  const shareText = `INDIAN-NEWS-21 ई-पेपर (${selected}) पढ़ें: `;
  const waUrl = `https://wa.me/?text=${encodeURIComponent(shareText + pdfUrl)}`;
  // Today's e-paper is ALWAYS considered available (backend gracefully falls back).
  const hasIssue = isToday ? true : !!available[selected];

  const download = (e) => {
    if (!hasIssue) { e.preventDefault(); toast.error("इस तारीख का ई-पेपर उपलब्ध नहीं"); }
  };

  const prev = () => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1));
  const next = () => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1));

  return (
    <>
      <PublicHeader/>
      <section className="bg-gradient-to-br from-[#0F172A] via-[#111827] to-[#1E3A8A] text-white">
        <div className="max-w-6xl mx-auto px-4 py-10 flex items-center gap-4">
          <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-amber-400 to-amber-600 text-[#0F172A] flex items-center justify-center shadow-lg">
            <Newspaper className="w-7 h-7"/>
          </div>
          <div>
            <div className="uppercase tracking-[0.28em] text-[10px] text-amber-300 font-black">E-Paper Archive</div>
            <h1 className="font-devnagri-heading text-3xl md:text-4xl font-black">ई-पेपर संग्रह</h1>
            <p className="text-slate-300 text-sm mt-1">पिछले 90 दिनों में से किसी भी तारीख़ का पूरा अख़बार PDF डाउनलोड कीजिये</p>
          </div>
        </div>
      </section>
      <main className="max-w-6xl mx-auto px-4 py-10 grid grid-cols-1 lg:grid-cols-3 gap-8" data-testid="epaper-archive-page">
        {/* Calendar */}
        <div className="lg:col-span-2 bg-white rounded-xl border border-slate-200 shadow-sm p-5">
          <div className="flex items-center justify-between mb-4">
            <button onClick={prev} className="p-2 rounded-full hover:bg-slate-100" data-testid="cal-prev"><ChevronLeft className="w-5 h-5"/></button>
            <div className="font-devnagri-heading font-black text-xl text-[#0F172A]">{MONTH_HI[cursor.getMonth()]} {cursor.getFullYear()}</div>
            <button onClick={next} className="p-2 rounded-full hover:bg-slate-100" data-testid="cal-next"><ChevronRight className="w-5 h-5"/></button>
          </div>
          <div className="grid grid-cols-7 gap-1.5 text-center mb-2">
            {DAY_HI.map((d) => <div key={d} className="text-[10px] uppercase tracking-widest text-slate-500 font-bold py-1">{d}</div>)}
          </div>
          <div className="grid grid-cols-7 gap-1.5">
            {days.map((d, i) => {
              if (!d) return <div key={`e-${i}`}></div>;
              const key = ymd(d);
              const has = !!available[key];
              const isSel = key === selected;
              const isFuture = d > new Date();
              const isTdy = key === todayYmd;
              return (
                <button key={key} disabled={isFuture}
                  onClick={() => setSelected(key)}
                  className={`aspect-square rounded-md text-sm transition relative ${
                    isSel ? "bg-[#0F172A] text-white font-black shadow" :
                    has ? "bg-amber-50 text-[#0F172A] hover:bg-amber-100 border border-amber-200 font-bold" :
                    isFuture ? "text-slate-300 cursor-not-allowed" :
                    "text-slate-500 hover:bg-slate-50"
                  } ${isTdy && !isSel ? "ring-2 ring-emerald-400" : ""}`}
                  data-testid={`cal-day-${key}`}>
                  {d.getDate()}
                  {has && !isSel && <span className="absolute bottom-1 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-amber-500"></span>}
                </button>
              );
            })}
          </div>
          <div className="mt-4 flex flex-wrap items-center gap-3 text-[11px] text-slate-500">
            <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-amber-100 border border-amber-300 inline-block"></span>ई-पेपर उपलब्ध</span>
            <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm ring-2 ring-emerald-400 inline-block"></span>आज</span>
            <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-[#0F172A] inline-block"></span>चुनी हुई तारीख़</span>
          </div>
        </div>

        {/* Selected date panel */}
        <aside className="bg-gradient-to-br from-[#0F172A] to-[#1E3A8A] text-white rounded-xl p-6 shadow-lg" data-testid="epaper-selected-panel">
          <div className="uppercase tracking-[0.25em] text-[10px] text-amber-300 font-black mb-2 flex items-center gap-1.5"><CalendarIcon className="w-3.5 h-3.5"/>चुनी हुई तारीख़</div>
          <div className="font-devnagri-heading font-black text-2xl leading-tight mb-1">
            {new Date(selected).toLocaleDateString("hi-IN", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
          </div>
          <div className="text-slate-400 text-xs mb-5">{selected}</div>
          {isToday ? (
            <div className="bg-emerald-500/15 border border-emerald-400/30 text-emerald-200 text-xs rounded-md px-3 py-2 mb-4">
              ✓ आज का ई-पेपर — नवीनतम {Math.max(available[selected] || 0, 15)} ख़बरें (LIVE)
            </div>
          ) : hasIssue ? (
            <div className="bg-emerald-500/15 border border-emerald-400/30 text-emerald-200 text-xs rounded-md px-3 py-2 mb-4">
              ✓ {available[selected]} ख़बरें प्रकाशित — ई-पेपर उपलब्ध है
            </div>
          ) : (
            <div className="bg-red-500/15 border border-red-400/30 text-red-200 text-xs rounded-md px-3 py-2 mb-4">
              इस तारीख़ का ई-पेपर उपलब्ध नहीं — कोई प्रकाशित समाचार नहीं मिला
            </div>
          )}
          <a href={pdfUrl} target="_blank" rel="noopener noreferrer" onClick={download}
            className={`inline-flex items-center gap-2 w-full justify-center px-4 py-3 rounded-md font-bold text-sm shadow-lg mb-2 transition ${
              hasIssue ? "bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-[#0F172A]" : "bg-slate-600 text-slate-400 cursor-not-allowed"
            }`} data-testid="epaper-archive-download">
            <FileDown className="w-4 h-4"/> PDF डाउनलोड करें
          </a>
          <a href={waUrl} target="_blank" rel="noopener noreferrer"
            className={`inline-flex items-center gap-2 w-full justify-center px-4 py-2.5 rounded-md font-bold text-sm transition ${
              hasIssue ? "bg-emerald-500 hover:bg-emerald-400 text-white" : "bg-slate-600 text-slate-400 pointer-events-none"
            }`} data-testid="epaper-archive-whatsapp">
            <MessageCircle className="w-4 h-4"/> WhatsApp पर भेजें
          </a>
          <Link to="/" className="mt-4 block text-center text-[11px] text-slate-400 hover:text-white">← मुखपृष्ठ</Link>
        </aside>
      </main>
      <PublicFooter/>
    </>
  );
}
